import React, { ReactElement } from 'react'
import BrandLogoIcon from 'src/components/BrandMarkLogo/BrandLogoIcon'
import ReceiptAgentAvatar from 'src/components/ReceiptAgentAvatar/ReceiptAgentAvatar'
import UserAvatar from 'src/components/UserAvatar/UserAvatar'
import { AvatarSizes } from 'src/enums'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'
import { useNullSafeLocale } from 'src/localization/useNullSafeLocale'
import { TransactionActivity } from 'src/queries/transactionActivities/transactionActivities.models'

import {
  ReceiptRecoveryMockActivity,
  ReceiptRecoveryMockActivityText,
} from './receiptRecoveryMockActivities'
import {
  getActorInitials,
  getTransactionActivityPresentation,
  LocalizedText,
  TransactionActivityPresentation,
} from './transactionActivityPresentation'
import * as style from './TransactionActivityTimeline.style'

interface TransactionActivityTimelineProps {
  activities: TransactionActivity[]
  merchantName?: string
  receiptRecoveryMockActivities?: ReceiptRecoveryMockActivity[]
  showReceiptAgentAvatar?: boolean
}

const formatOccurredOn = (occurredAt: string, locale: string): string => {
  const date = new Date(occurredAt)

  if (Number.isNaN(date.getTime())) {
    return occurredAt
  }

  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  }).format(date)
}

const translateReceiptRecoveryMockActivityText = (
  translate: ReturnType<typeof useTranslate>,
  text: ReceiptRecoveryMockActivityText,
): string => {
  switch (text) {
    case 'Receipt Agent':
      return translate('Receipt Agent')
    case 'Requested a receipt from the cardholder':
      return translate('Requested a receipt from the cardholder')
    case 'Run #1':
      return translate('Run #1')
    case 'Recovered ACME_HOTEL_0418.pdf':
      return translate('Recovered ACME_HOTEL_0418.pdf')
    case 'From the organisation’s Google Workspace mail · run #4':
      return translate('From the organisation’s Google Workspace mail · run #4')
    case 'No receipt was found in connected sources':
      return translate('No receipt was found in connected sources')
    case 'Run #4':
      return translate('Run #4')
    case 'Local preview':
      return translate('Local preview')
    case 'Confirmed the receipt matches this transaction':
      return translate('Confirmed the receipt matches this transaction')
    case 'Receipt Reconciled':
      return translate('Receipt Reconciled')
  }
}

const getActivityMerchantName = (
  activity: TransactionActivity,
  fallbackMerchantName?: string,
): string | undefined => {
  const { payload } = activity

  if (
    typeof payload === 'object' &&
    payload !== null &&
    'merchantName' in payload &&
    typeof payload.merchantName === 'string' &&
    payload.merchantName.trim().length > 0
  ) {
    return payload.merchantName
  }

  return fallbackMerchantName
}

interface TransactionActivityRowProps {
  activity: TransactionActivity
  formattedAmount: string | null
  formattedOccurredOn: string
  merchantName?: string
  presentation: TransactionActivityPresentation
}

const translateActivityText = (
  translate: ReturnType<typeof useTranslate>,
  text: LocalizedText,
): string => {
  switch (text.source) {
    case 'Activity updated':
      return translate('Activity updated')
    case 'Spent':
      return translate('Spent')
    case 'Transaction was declined':
      return translate('Transaction was declined')
    case 'Transaction cleared':
      return translate('Transaction cleared')
    case 'Transaction was reversed':
      return translate('Transaction was reversed')
    case 'Automatically matched a receipt':
      return translate('Automatically matched a receipt')
    case 'Automatically matched a receipt from Gmail Integration with this transaction':
      return translate(
        'Automatically matched a receipt from Gmail Integration with this transaction',
      )
    case 'Matched receipt to this transaction':
      return translate('Matched receipt to this transaction')
    case 'Sent a receipt reminder':
      return translate('Sent a receipt reminder')
    case 'Attached a receipt':
      return translate('Attached a receipt')
    case 'Moved receipt':
      return translate('Moved receipt')
    case 'Removed receipt':
      return translate('Removed receipt')
    case 'Checked previously submitted receipts':
      return translate('Checked previously submitted receipts')
    case 'No candidates found':
      return translate('No candidates found')
    case 'Found 1 candidate':
      return translate('Found 1 candidate')
    case 'Found ${candidateCount} candidates':
      return translate('Found ${candidateCount} candidates', {
        candidateCount: text.values.candidateCount,
      })
    case 'Assessed a previously submitted receipt':
      return translate('Assessed a previously submitted receipt')
    case 'Needs review':
      return translate('Needs review')
    case 'No reliable match found':
      return translate('No reliable match found')
    case 'Recovery held':
      return translate('Recovery held')
    case 'Finished receipt recovery':
      return translate('Finished receipt recovery')
    case 'Waiting on spender':
      return translate('Waiting on spender')
    case 'Receipt matched':
      return translate('Receipt matched')
    case 'Recovery skipped':
      return translate('Recovery skipped')
    case 'Recovery failed':
      return translate('Recovery failed')
    case 'Updated Notes':
      return translate('Updated Notes')
    case 'Removed Notes':
      return translate('Removed Notes')
    case 'Comment':
      return translate('Comment')
    case 'Left a comment':
      return translate('Left a comment')
    case 'Comment unavailable':
      return translate('Comment unavailable')
    case 'Updated expense category':
      return translate('Updated expense category')
    case 'Updated expense categories':
      return translate('Updated expense categories')
    case 'Updated ${categoryName} expense category:':
      return translate('Updated ${categoryName} expense category:', {
        categoryName: text.values.categoryName,
      })
    case 'Updated expense fields':
      return translate('Updated expense fields')
    case 'Updated ${fieldName}:':
      return translate('Updated ${fieldName}:', {
        fieldName: text.values.fieldName,
      })
    case 'Split ${splitNumber}':
      return translate('Split ${splitNumber}', {
        splitNumber: text.values.splitNumber,
      })
    case 'Updated manager review:':
      return translate('Updated manager review:')
    case 'Updated final review:':
      return translate('Updated final review:')
    case 'Rejected transaction':
      return translate('Rejected transaction')
    case 'Removed rejection note':
      return translate('Removed rejection note')
    case 'Auto-reviewed':
      return translate('Auto-reviewed')
    case 'Reviewed':
      return translate('Reviewed')
    case 'Rejected':
      return translate('Rejected')
    case 'Pending Review':
      return translate('Pending Review')
    case 'Receipt Missing':
      return translate('Receipt Missing')
    case 'Receipt Incorrect':
      return translate('Receipt Incorrect')
    case 'Expense Category Missing':
      return translate('Expense Category Missing')
    case 'Expense Category Incorrect':
      return translate('Expense Category Incorrect')
    case 'Note/Justification Missing':
      return translate('Note/Justification Missing')
    case 'Not a business expense':
      return translate('Not a business expense')
    case 'Other':
      return translate('Other')
    case 'Flagged':
      return translate('Flagged')
    case 'Pending Approval':
      return translate('Pending Approval')
    case 'Approved':
      return translate('Approved')
  }
}

const TransactionActivityRow = ({
  activity,
  formattedAmount,
  formattedOccurredOn,
  merchantName,
  presentation,
}: TransactionActivityRowProps): ReactElement => {
  const translate = useTranslate()
  const isReceiptAgent =
    activity.actor.type === 'AGENT' &&
    activity.actor.displayName === 'Receipt Agent'
  const isExtendActor =
    !isReceiptAgent &&
    (activity.actor.type === 'SYSTEM' || activity.actor.type === 'AGENT')
  const actorDisplayName =
    isExtendActor &&
    ['Extend', 'Extend AI'].includes(activity.actor.displayName)
      ? 'Extend'
      : activity.actor.displayName
  const actor = {
    id: activity.actor.id ?? activity.id,
    fullNameOrEmail: actorDisplayName,
    initials: getActorInitials(actorDisplayName),
    avatarUrl: activity.actor.avatarUrl ?? undefined,
    avatarColor: activity.actor.avatarColor,
  }
  const displayChangeValue = (value: string | null): string =>
    value === null ? translate('Missing') : value
  const isComment =
    activity.type === 'conversation.message.posted' &&
    presentation.details.some((detail) => detail.kind === 'quote')
  const activityMerchantName = getActivityMerchantName(activity, merchantName)
  const eventTitle =
    activity.type === 'transaction.authorized' && formattedAmount
      ? activityMerchantName
        ? translate('Spent ${amount} at ${merchantName}', {
            amount: formattedAmount,
            merchantName: activityMerchantName,
          })
        : translate('Spent ${amount}', { amount: formattedAmount })
      : activity.type === 'transaction.declined' && activityMerchantName
        ? translate('The transaction at ${merchantName} was declined', {
            merchantName: activityMerchantName,
          })
        : translateActivityText(translate, presentation.title)
  return (
    <li css={style.activity}>
      {isReceiptAgent ? (
        <span css={style.avatar}>
          <ReceiptAgentAvatar testId={'receipt-agent-activity-avatar'} />
        </span>
      ) : isExtendActor ? (
        <BrandLogoIcon aria-hidden css={style.avatar} size={24} />
      ) : (
        <UserAvatar
          css={style.avatar}
          decorative
          size={AvatarSizes.Small24}
          user={actor}
        />
      )}
      <div css={style.content}>
        <div css={style.actorRow}>
          <span css={style.actorName}>{actorDisplayName}</span>
          <span css={style.metadataSeparator} aria-hidden="true">
            {'•'}
          </span>
          <time css={style.occurredAt} dateTime={activity.occurredAt}>
            {formattedOccurredOn}
          </time>
        </div>
        {!isComment && (
          <div css={style.event}>
            <span
              css={style.eventTitle}
              data-testid="transaction-activity-title"
            >
              {eventTitle}
            </span>
          </div>
        )}
        {presentation.details.length > 0 && (
          <div css={style.details}>
            {presentation.details.map((detail, index) => {
              if (detail.kind === 'quote') {
                if (isComment) {
                  return (
                    <div css={style.commentCard} key={`quote-${index}`}>
                      <p css={style.comment}>
                        {detail.value}
                        {detail.truncated && '…'}
                      </p>
                    </div>
                  )
                }

                return (
                  <q css={style.quote} key={`quote-${index}`}>
                    {detail.value}
                    {detail.truncated && '…'}
                  </q>
                )
              }

              if (detail.kind === 'comment') {
                return (
                  <p css={style.reviewReason} key={`comment-${index}`}>
                    {detail.value}
                    {detail.truncated && '…'}
                  </p>
                )
              }

              if (detail.kind === 'change') {
                const before = displayChangeValue(detail.before)
                const after = displayChangeValue(detail.after)
                const label = detail.label
                  ? translateActivityText(translate, detail.label)
                  : undefined
                const changeDescription = translate(
                  'Changed from ${before} to ${after}',
                  { before, after },
                )
                const accessibleChangeDescription = label
                  ? `${label}. ${changeDescription}`
                  : changeDescription

                return (
                  <p css={style.change} key={`change-${index}`}>
                    <span css={style.visuallyHidden}>
                      {accessibleChangeDescription}
                    </span>
                    <span aria-hidden="true">
                      {label && <span css={style.detailLabel}>{label}</span>}
                      {before}
                      {' → '}
                      {after}
                    </span>
                  </p>
                )
              }

              return (
                <div css={style.status} key={`status-${index}`}>
                  <p>{translateActivityText(translate, detail.value)}</p>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </li>
  )
}

const ReceiptRecoveryMockActivityRow = ({
  activity,
  formattedOccurredOn,
  showReceiptAgentAvatar,
}: {
  activity: ReceiptRecoveryMockActivity
  formattedOccurredOn: string
  showReceiptAgentAvatar: boolean
}): ReactElement => {
  const translate = useTranslate()
  const isReceiptAgent =
    showReceiptAgentAvatar && activity.actor.type === 'RECEIPT_AGENT'
  const actorDisplayName = translateReceiptRecoveryMockActivityText(
    translate,
    activity.actor.displayName,
  )
  const activityTitle = translateReceiptRecoveryMockActivityText(
    translate,
    activity.title,
  )
  const activityDetail = activity.detail
    ? translateReceiptRecoveryMockActivityText(translate, activity.detail)
    : undefined
  const actor = {
    id: activity.id,
    fullNameOrEmail: actorDisplayName,
    initials: getActorInitials(actorDisplayName),
  }

  return (
    <li css={style.activity}>
      {isReceiptAgent ? (
        <span css={style.avatar}>
          <ReceiptAgentAvatar testId={'receipt-agent-activity-avatar'} />
        </span>
      ) : activity.actor.type === 'RECEIPT_AGENT' ? (
        <BrandLogoIcon aria-hidden css={style.avatar} size={24} />
      ) : (
        <UserAvatar
          css={style.avatar}
          decorative
          size={AvatarSizes.Small24}
          user={actor}
        />
      )}
      <div css={style.content}>
        <div css={style.actorRow}>
          <span css={style.actorName}>{actorDisplayName}</span>
          <span css={style.metadataSeparator} aria-hidden="true">
            {'•'}
          </span>
          <time css={style.occurredAt} dateTime={activity.occurredAt}>
            {formattedOccurredOn}
          </time>
        </div>
        <div css={style.event}>
          <span css={style.eventTitle}>{activityTitle}</span>
        </div>
        {activityDetail && <p css={style.reviewReason}>{activityDetail}</p>}
      </div>
    </li>
  )
}

const TransactionActivityTimeline = ({
  activities,
  merchantName,
  receiptRecoveryMockActivities,
  showReceiptAgentAvatar = false,
}: TransactionActivityTimelineProps): ReactElement => {
  const translate = useTranslate()
  const formatCurrency = useCurrencyFormatter()
  const locale = useNullSafeLocale()

  return (
    <ol css={style.timeline} aria-label={translate('Transaction activity')}>
      {receiptRecoveryMockActivities?.map((activity) => (
        <ReceiptRecoveryMockActivityRow
          key={activity.id}
          activity={activity}
          formattedOccurredOn={formatOccurredOn(activity.occurredAt, locale)}
          showReceiptAgentAvatar={showReceiptAgentAvatar}
        />
      ))}
      {!receiptRecoveryMockActivities &&
        activities.map((activity) => {
          const presentation = getTransactionActivityPresentation(activity)
          const formattedAmount = presentation.money
            ? formatCurrency({
                amount: presentation.money.amountCents / 100,
                currency: presentation.money.currency,
                maximumFractionDigits: 2,
              })
            : null

          return (
            <TransactionActivityRow
              key={activity.id}
              activity={activity}
              formattedAmount={formattedAmount}
              formattedOccurredOn={formatOccurredOn(
                activity.occurredAt,
                locale,
              )}
              merchantName={merchantName}
              presentation={presentation}
            />
          )
        })}
    </ol>
  )
}

export default TransactionActivityTimeline
